import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { distinctUntilChanged, filter, map, take } from 'rxjs';
import { ConfigService } from '../config-service/config-service.service';

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class GoogleAnalyticsService {
  private gtmId?: string;
  private measurementId?: string;
  private initialized = false;
  private lastPagePath?: string;
  private routeListenerAttached = false;
  private utmParams: Record<string, string> = {};

  constructor(private configService: ConfigService, private router: Router) {
    this.configService
      .getConfig()
      .pipe(take(1))
      .subscribe((config: any) => {
        this.measurementId = config.googleMeasurementId;
        this.gtmId = config.googleTagManagerId;
        const cookiebotId = config.cookiebotId;
        const isFlutterApp = (window as any).flutter_inappwebview != null;

        // Cookiebot is NOT active → always allow GA
        const isCookiebotActive = !!cookiebotId && !isFlutterApp;

        // If Cookiebot *is* active → wait for user consent
        if (isCookiebotActive) {
          // Wait for Cookiebot to be loaded first
          this.waitForCookiebotLoad().then(() => {
            this.waitForCookiebotConsent();
          });
        } else {
          // No Cookiebot → run GA immediately
          this.enableGA();
        }
      });
  }

  private waitForCookiebotLoad(): Promise<void> {
    return new Promise((resolve) => {
      // Check if Cookiebot script already exists
      if (document.getElementById('Cookiebot')) {
        resolve();
        return;
      }

      // Poll for Cookiebot script to be added
      const checkInterval = setInterval(() => {
        if (document.getElementById('Cookiebot')) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        resolve();
      }, 10000);
    });
  }

  private waitForCookiebotConsent() {
    // Otherwise defer GA until consent is known
    window.addEventListener('CookiebotOnConsentReady', () => {
      this.handleCookiebotConsent();
    });

    setTimeout(() => {
      // If Cookiebot already decided
      if ((window as any).Cookiebot && (window as any).Cookiebot.hasResponse) {
        this.handleCookiebotConsent();
        return;
      }
    }, 1000);
  }

  private handleCookiebotConsent() {
    const hasStatsConsent = (window as any).Cookiebot?.consent?.statistics === true;

    if (hasStatsConsent) {
      this.enableGA();
    } else {
      console.log('GA blocked: user denied statistics consent');
    }
  }

  private enableGA() {
    if (this.initialized || (!this.measurementId && !this.gtmId)) return;

    this.initGoogleAnalytic(this.measurementId, this.gtmId);
    this.listenToRouteChanges();
    this.initialized = true;
  }

  private initGoogleAnalytic(measurementId?: string, gtmId?: string) {
    // Prioritize GTM over GA4 if both are present
    if (gtmId) {
      // Load GTM script
      const script = document.createElement('script');
      script.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtmId}');`;
      document.head.prepend(script);

      // Insert GTM noscript fallback after body tag
      document.body.insertAdjacentHTML(
        'afterbegin',
        `<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
        height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>`,
      );
    } else if (measurementId) {
      // Load GA4 script only if no GTM
      const script1 = document.createElement('script');
      script1.async = true;
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(script1);

      // Initialize GA4 with dynamic ID
      const script2 = document.createElement('script');
      script2.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${measurementId}', { send_page_view: false });`;
      document.head.appendChild(script2);

      this.extractAndSetUtmParameters();
    }
    this.initialized = true;
  }

  private listenToRouteChanges() {
    if (this.routeListenerAttached) return;
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e) => e.urlAfterRedirects),
        distinctUntilChanged(),
      )
      .subscribe((url) => {
        this.sendPageView(url);
      });
    this.routeListenerAttached = true;
  }

  /**
   * Send a page_view event to GA4.
   */
  private sendPageView(page_path: string) {
    if (!this.measurementId || !window.gtag) return;
    if (this.lastPagePath === page_path) return; // Prevent duplicate
    window.gtag('event', 'page_view', { page_path });
    this.lastPagePath = page_path;
  }

  /**
   * Track a custom event
   * @param eventName GA4 recommended event name
   * @param params Optional event parameters (category, label, value, etc.)
   */
  public sendEvent(eventName: string, params?: Record<string, any>) {
    if (this.measurementId && window.gtag) {
      window.gtag('event', eventName, params);
    }
  }

  public sendToGoogleTagManager(eventName: string, params?: Record<string, unknown>) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...(params ?? {}),
    });
  }

  private extractAndSetUtmParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams: Record<string, string> = {};

    urlParams.forEach((value, key) => {
      if (key.startsWith('utm_') && value) {
        utmParams[key] = value;
      }
    });

    if (Object.keys(utmParams).length > 0) {
      this.utmParams = utmParams;
      this.setUtmParameters();
    }
  }

  private setUtmParameters() {
    if (!this.measurementId || !window.gtag) return;
    window.gtag('config', this.measurementId, {
      send_page_view: false,
      ...this.getCampaignParams(),
    });
  }

  private getCampaignParams(): { campaign: Record<string, any> } {
    return {
      campaign: {
        id: this.utmParams['utm_id'] || undefined,
        source: this.utmParams['utm_source'] || undefined,
        medium: this.utmParams['utm_medium'] || undefined,
        name: this.utmParams['utm_campaign'] || undefined,
        term: this.utmParams['utm_term'] || undefined,
        content: this.utmParams['utm_content'] || undefined,
      },
    };
  }

  // Get Campaign Data
  public getCampaignData() {
    if ((!this.measurementId && !this.gtmId) || !window.gtag) return;
    return {
      ga_campaign_id: this.utmParams?.['utm_id'],
      ga_campaign_source: this.utmParams?.['utm_source'],
      ga_campaign_medium: this.utmParams?.['utm_medium'],
      ga_campaign_name: this.utmParams?.['utm_campaign'],
      ga_campaign_term: this.utmParams?.['utm_term'],
      ga_campaign_content: this.utmParams?.['utm_content'],
    };
  }

  /**
   * Get the full GA client ID from the _ga cookie
   */
  public getFullGAClientId(): string | null | undefined {
    if ((!this.measurementId && !this.gtmId) || !window.gtag) return;
    const match = document.cookie.match(/_ga=GA\d\.\d\.\d+\.\d+/);
    if (match) {
      return match[0].split('=')[1]; // Returns "GA1.1.1911947541.1749596555"
    }
    return null;
  }
}
